import React, { useState } from 'react';
import CustomParagraph from './CustomParagraph';
import CustomButton from './CustomButton';
import { Button, Divider, Flex, Menu, message } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import phoneAi from '../../src/assets/images/Aiphone.svg';
import UseAi from '../assets/images/UseAi';
import ExpandMore from '../assets/images/ExpandMore';
import { marked } from 'marked';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

import { RightOutlined } from '@ant-design/icons';
import { getFormattedUserTimeZone } from '@/common/utils/utils';
import { useToken } from '@/common/helper';
import ResolutionWindow from './ResolutionWindow';
import { AddDetialIcon, AIEmailIcon } from '@/assets/icons/icons';
import { AI_EMAIL_TIMEOUT_MINUTES } from '@/common/constants';
import { contact_type } from '@/pages/Shipments/components/ShipmentAlerts';
import { primaryContact } from '@/apis/types';
import { useUserDateTimeFormat } from '@/hooks';
import './StatusCardNotification.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

interface AiOption {
  key: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}

interface TransportContact {
  email?: string;
  company_courier_id?: string | number;
  name?: string;
}

interface Courier {
  title?: string;
}

interface Shipment {
  tp_primary_contact?: TransportContact;
  schedule_viability_window?: string;
  courier?: Courier | null;
}

interface StatusCardNotificationProps {
  sharePage?: boolean;
  timezone?: string;
  showButtons?: boolean;
  showDismissButton?: boolean;
  cardStyle?: 'style-1' | 'style-2';
  icon?: React.ReactNode;
  createdAt: string;
  title: string;
  description?: string;
  shipment?: Shipment;
  bgColor?: string;
  color?: string;

  actionButtonText?: string;
  showActionBtn?: boolean;
  actionButtonBorderRadius?: string;
  actionButtonBgColor?: string;
  actionButtonTextColor?: string;
  dismissButtonTextColor?: string;
  actionButtonOnClick?: () => void;
  dismissAction?: () => void;
  aiOptions?: AiOption[];
  aiStatus?: 'ai_call_in_progress' | 'ai_email_in_progress' | string;
  useAiButton?: boolean;
  isCritical?: boolean;
  onCardClick?: () => void;
  alertType?: string;
  is_TP_exception?: boolean;
  AiSummary?: string;
  recommendedAction?: string;
  contactType?: contact_type;
  primaryContact?: primaryContact;
  aiStartTime?: string | null;
  onCancelAiEmail?: () => void;
  onExpireAiEmail?: () => void;
  leadingAction?: React.ReactNode;
  expandedContent?: React.ReactNode;
}

interface TimeFormatResult {
  relative: string;
  full: string;
}

/**
 * StatusCardNotification displays notification cards with various styles and AI integration features
 *
 * @param showButtons - Whether to show action buttons
 * @param cardStyle - Layout style: 'style-1' (horizontal) or 'style-2' (vertical with dismiss)
 * @param icon - Icon to display in the notification
 * @param createdAt - Timestamp for the notification
 * @param title - Notification title
 * @param description - Notification description (supports markdown)
 * @param shipment - Shipment data for AI features
 * @param bgColor - Background color
 * @param color - Text color
 * @param actionButtonText - Text for the action button
 * @param actionButtonBorderRadius - Border radius for action button
 * @param actionButtonBgColor - Background color for action button
 * @param actionButtonOnClick - Action button click handler
 * @param dismissAction - Dismiss button click handler
 * @param aiOptions - AI dropdown options
 * @param aiStatus - Current AI status
 * @param useAiButton - Whether to show AI button
 * @param isCritical - Whether notification is critical (affects AI button styling)
 */
/**
 * Formats alert_type string for display (e.g., "low_battery_at_origin" -> "Low Battery At Origin")
 */
const formatAlertType = (alertType?: string): string => {
  if (!alertType) return '';
  return alertType
    .split('_')
    .map((word) => word.charAt(0).toLowerCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const StatusCardNotification: React.FC<StatusCardNotificationProps> = ({
  sharePage = false,
  timezone,
  showButtons = true,
  showDismissButton = true,
  cardStyle,
  icon,
  createdAt,
  title,
  description,
  shipment,
  bgColor,
  color,
  actionButtonText,
  showActionBtn,
  actionButtonBorderRadius,
  actionButtonBgColor,
  actionButtonTextColor,
  dismissButtonTextColor,
  actionButtonOnClick,
  dismissAction,
  aiOptions = [],
  aiStatus,
  useAiButton,
  onCardClick,
  alertType,
  is_TP_exception,
  AiSummary, // for TP case after AI action happens
  recommendedAction, // for transport provider case
  contactType, // show relevent message on add detail dropdown
  primaryContact,
  aiStartTime,
  onCancelAiEmail,
  onExpireAiEmail,
  isCritical,
  leadingAction,
  expandedContent,
}) => {
  const navigate = useNavigate();

  const { formatDateTime } = useUserDateTimeFormat();
  const { decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const SUMMARY_STATES = [
    'ai_call_in_progress',
    'ai_email_in_progress',
    'ai_call_completed',
    'ai_email_completed',
  ] as string[];

  const showSummary = !!aiStatus && SUMMARY_STATES.includes(aiStatus);
  // Check if AI email timer has expired (even if status hasn't been updated yet)
  const isEmailTimerExpired = React.useMemo(() => {
    if (aiStatus !== 'ai_email_in_progress' || !aiStartTime) return false;
    try {
      const startTime = dayjs.utc(aiStartTime);
      const now = dayjs.utc();
      const elapsedSeconds = now.diff(startTime, 'second');
      const timeoutSeconds = AI_EMAIL_TIMEOUT_MINUTES * 60;
      return elapsedSeconds >= timeoutSeconds;
    } catch {
      return false;
    }
  }, [aiStatus, aiStartTime]);

  // Check if AI is in progress (same pattern as AI call)
  // For email: also check that timer hasn't expired
  const isAiInProgress =
    aiStatus === 'ai_call_in_progress' ||
    (aiStatus === 'ai_email_in_progress' && !isEmailTimerExpired);

  const processedDescription = description
    ? description.replace(/\\n/g, '\n')
    : '';

  const aiDropdownItems = aiOptions.map((item) => ({
    key: item.key,
    disabled: item.disabled,
    label: (
      <Flex align='center' gap={8}>
        <div
          className={
            item.disabled
              ? 'status-card-notification__menu-icon status-card-notification__menu-icon--disabled'
              : 'status-card-notification__menu-icon'
          }
        >
          {item.icon}
        </div>
        <span
          className={
            item.disabled
              ? 'status-card-notification__menu-label status-card-notification__menu-label--disabled'
              : 'status-card-notification__menu-label'
          }
        >
          {item.label}
        </span>
      </Flex>
    ),
  }));

  const handleRouteToAddContact = () => {
    const routing_id = contactType?.routing_key;
    if (routing_id) {
      if (contactType?.recommended_contact == 'transport provider') {
        const _ = navigate(
          `/settings/couriers?openDrawer=true&id=${routing_id}`
        );
      } else {
        const _ = navigate(
          `/settings/location?openDrawer=true&id=${routing_id}`
        );
      }
    } else {
      message.error('You have no permission');
    }
  };

  const menu = (
    <div className='custom-ai-dropdown'>
      <Menu
        items={aiDropdownItems}
        onClick={({ key }) => {
          const selectedItem = aiOptions.find((item) => item.key === key);
          if (selectedItem && !selectedItem.disabled) {
            selectedItem.onClick();
          }
        }}
      />

      {!primaryContact || !primaryContact?.email || !primaryContact?.phone ? (
        <>
          <Divider style={{ margin: '0px 0' }} />
          <div className='ai-dropdown-footer'>
            <div className='footer-text'>
              Add {contactType?.recommended_contact || ''} contact details to
              use AI
            </div>

            <button
              className='add-detail-btn'
              onClick={() => {
                handleRouteToAddContact();
              }}
            >
              <AddDetialIcon />
              Add details
            </button>
          </div>
        </>
      ) : null}
    </div>
  );

  const [showFull, setShowFull] = useState(false);
  dayjs.extend(relativeTime);

  const getFormattedTime = (
    ts: string,
    preferredTz?: string
  ): TimeFormatResult => {
    let userTz: string | undefined;

    if (sharePage) {
      userTz = preferredTz?.trim() || dayjs.tz.guess();
    } else if (isTimeZone) {
      const tzString = getFormattedUserTimeZone();
      const match = tzString.match(/ ([A-Za-z_/]+)$/);
      userTz = match ? match[1] : dayjs.tz.guess();
    }

    const targetTimeInTz = userTz
      ? dayjs.tz(ts, 'YYYY-MM-DD HH:mm:ss', userTz)
      : dayjs(ts, 'YYYY-MM-DD HH:mm:ss').local();

    const nowInTz = userTz ? dayjs().tz(userTz) : dayjs();

    const diffSeconds = nowInTz.diff(targetTimeInTz, 'second');
    const diffMinutes = Math.floor(Math.abs(diffSeconds) / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    let relativeTimeResult: string;
    if (diffDays >= 1) {
      relativeTimeResult =
        diffSeconds >= 0
          ? `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
          : `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours >= 1) {
      relativeTimeResult =
        diffSeconds >= 0
          ? `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`
          : `${diffHours} hr${diffHours > 1 ? 's' : ''}`;
    } else if (diffMinutes >= 1) {
      relativeTimeResult =
        diffSeconds >= 0
          ? `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
          : `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else {
      relativeTimeResult = 'just now';
    }

    const fullFormattedTime = formatDateTime(targetTimeInTz, {
      includeTime: true,
    });

    return {
      full: fullFormattedTime,
      relative: relativeTimeResult,
    };
  };

  const { relative, full } = getFormattedTime(createdAt, timezone);

  // Make card clickable only if AI email is in progress AND timer hasn't expired
  // Same pattern as AI call: only clickable when actually in progress
  const isClickable =
    onCardClick && aiStatus === 'ai_email_in_progress' && !isEmailTimerExpired;

  // Generate descriptive text for AI email in progress
  const getAiEmailDescription = (): string => {
    if (aiStatus !== 'ai_email_in_progress') return '';

    const contactName = primaryContact?.name || 'the contact';
    const companyName = shipment?.courier?.title || 'the transport provider';
    const formattedAlertType = formatAlertType(alertType) || 'the problem';

    return `The AI assistant is emailing ${contactName} from ${companyName} to correct the problem of ${formattedAlertType}.`;
  };

  const aiEmailDescription = getAiEmailDescription();

  const CardContent = (
    <Flex
      align='center'
      justify='space-between'
      gap={cardStyle === 'style-2' ? 12 : 16}
      vertical={cardStyle === 'style-2'}
      onClick={isClickable ? onCardClick : undefined}
      style={{
        padding: '16px',
        background: isAiInProgress
          ? 'var(--zl-ai-card-bg, linear-gradient(0deg, #ffffff 0%, #f2faff 100%))'
          : bgColor,
        borderRadius: isAiInProgress ? '9px' : '12px',
        cursor: isClickable ? 'pointer' : 'default',
        position: 'relative',
      }}
    >
      <Flex
        gap={16}
        align={isAiInProgress ? 'center' : 'flex-start'}
        style={{ width: '100%' }}
      >
        {aiStatus === 'ai_email_in_progress' && !isEmailTimerExpired ? (
          <div
            className='ai-email-icon-blue-large status-card-notification__no-shrink'
            role='img'
            aria-label='AI email in progress'
          >
            <AIEmailIcon />
          </div>
        ) : aiStatus === 'ai_call_in_progress' ? (
          <img
            className='zl-invert-on-dark status-card-notification__ai-call-icon'
            src={phoneAi}
            alt='AI call preview'
          />
        ) : (
          // Show regular icon for all other statuses (completed, failed, etc.)
          icon
        )}

        <Flex vertical gap={4} style={{ width: '100%', flex: 1 }}>
          <Flex
            justify='space-between'
            align='center'
            style={{ width: '100%' }}
          >
            <CustomParagraph
              text={title}
              fontSize='12px'
              fontWeight={600}
              lineHeight='20px'
              color={isAiInProgress ? 'black' : color}
              marginBottom='0'
            />
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowFull((prev) => !prev);
              }}
              className='status-card-notification__timestamp'
            >
              <CustomParagraph
                text={showFull ? full : relative}
                fontSize='12px'
                fontWeight={400}
                lineHeight='20px'
                color={isAiInProgress ? 'black' : color}
                marginBottom='0'
              />
            </span>
          </Flex>
          {is_TP_exception && (
            <div
              className={`alert-preview-message ${isCritical ? 'color-white' : ''} is-CT-alert `}
            >
              <span> {processedDescription}</span>
            </div>
          )}
          {aiStatus === 'ai_email_in_progress' &&
          !isEmailTimerExpired &&
          aiEmailDescription ? (
            <>
              <CustomParagraph
                text={aiEmailDescription}
                fontSize='12px'
                fontWeight={400}
                lineHeight='20px'
                color='black'
                marginBottom='8px'
                className='word-split'
              />
              <ResolutionWindow
                aiStartTime={aiStartTime}
                timeoutMinutes={AI_EMAIL_TIMEOUT_MINUTES}
                layout='inline'
                showCancelButton={false}
                onCancel={onCancelAiEmail}
                onExpire={onExpireAiEmail}
              />
            </>
          ) : (
            <CustomParagraph
              htmlString={
                is_TP_exception
                  ? showSummary
                    ? AiSummary
                    : recommendedAction
                  : processedDescription
                    ? (marked(processedDescription, { breaks: true }) as string)
                    : ''
              }
              fontSize='12px'
              fontWeight={400}
              lineHeight='20px'
              color={isAiInProgress ? 'black' : color}
              marginBottom='0'
              className='word-split'
            />
          )}
        </Flex>

        {isClickable && (
          <RightOutlined
            style={{
              fontSize: '16px',
              color: 'var(--text-primary)',
              flexShrink: 0,
              alignSelf: 'center',
            }}
          />
        )}
      </Flex>

      {expandedContent ? (
        <div className='status-card-notification__full-width'>
          {expandedContent}
        </div>
      ) : (
        <>
          {isAiInProgress && leadingAction && (
            <Flex style={{ width: '100%' }}>{leadingAction}</Flex>
          )}

          {!isAiInProgress && (
            <>
              {showButtons === true && cardStyle === 'style-1' && (
                <CustomButton
                  text={actionButtonText}
                  width='auto'
                  padding='5px 15px'
                  height='36px'
                  color={actionButtonTextColor}
                  bgColor={actionButtonBgColor}
                  borderRadius={actionButtonBorderRadius}
                  marginBottom='0'
                  boxShadow='none'
                  onClick={(e) => {
                    e.stopPropagation();
                    actionButtonOnClick?.();
                  }}
                />
              )}

              {leadingAction && cardStyle !== 'style-2' && (
                <Flex style={{ width: '100%' }}>{leadingAction}</Flex>
              )}

              {cardStyle === 'style-2' && (
                <Flex
                  className='alert-card-action-row gap-8-responsive'
                  align='center'
                  wrap
                  style={{ width: '100%' }}
                  justify='flex-end'
                  onClick={(e) => e.stopPropagation()}
                >
                  {leadingAction}
                  <Flex
                    className='alert-card-secondary-actions gap-8-responsive'
                    justify='flex-end'
                    align='center'
                    wrap
                    style={{ marginLeft: 'auto', maxWidth: '100%' }}
                  >
                    {showDismissButton && (
                      <CustomButton
                        text='Dismiss'
                        width='auto'
                        className='alert-card-btn-text'
                        fontWeight={'600'}
                        height='36px'
                        color={dismissButtonTextColor}
                        bgColor={'initial'}
                        boxShadow={'none'}
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissAction?.();
                        }}
                      />
                    )}
                    {aiOptions?.length > 0 && useAiButton && (
                      <Dropdown
                        overlay={menu}
                        placement='bottomRight'
                        className='useAiDropdown'
                      >
                        <Button
                          className='alert-card-btn-text'
                          style={{
                            height: '36px',
                            backgroundColor: actionButtonBgColor,
                            borderRadius: '8px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            border: 'none',
                            boxShadow: 'none',
                            outline: 'none',
                          }}
                        >
                          <UseAi color={actionButtonTextColor} />
                          <div
                            // eslint-disable-next-line react/forbid-dom-props -- the text colour comes from the actionButtonTextColor prop at runtime
                            style={{
                              fontWeight: 600,
                              color: actionButtonTextColor,
                            }}
                          >
                            Use AI
                          </div>
                          <ExpandMore color={actionButtonTextColor} />
                        </Button>
                      </Dropdown>
                    )}

                    {showActionBtn && (
                      <CustomButton
                        text={actionButtonText}
                        width='auto'
                        fontWeight={'600'}
                        className='alert-card-btn-text'
                        height='36px'
                        color={actionButtonTextColor}
                        bgColor={actionButtonBgColor}
                        borderRadius={actionButtonBorderRadius}
                        marginBottom='0'
                        boxShadow='none'
                        onClick={(e) => {
                          e.stopPropagation();
                          actionButtonOnClick?.();
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
              )}
            </>
          )}
        </>
      )}
    </Flex>
  );

  return isAiInProgress ? (
    <div className='status-card-notification__ai-frame'>{CardContent}</div>
  ) : (
    CardContent
  );
};

export default StatusCardNotification;
